import { Form, Radio } from 'antd';
import { useContext, useEffect } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import type { OpeningConfigurationType } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import { OPENING_CONFIGURATION_TYPE } from 'app/typings/core/admin/experiences/enum/openingConfiguration';
import type { OpeningConfiguration } from 'app/typings/core/common/openingConfiguration/openingConfiguration';
import type { BasePriceConfiguration } from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import {
  PRICE_COST_TYPE,
  PRICE_DIFFERENTIATION_MODE,
} from 'app/typings/core/common/openingConfiguration/priceConfigurationType';
import { ExperienceType } from 'app/typings/experiences';

import { ConstantPriceConfiguration } from './_components/ConstantPriceConfiguration';
import { DailyPriceConfiguration } from './_components/DailyPriceConfiguration';

const RULES = [{ required: true, message: 'Required' }];

export const PriceConfiguration = () => {
  const { isViewMode } = useContext(ViewEditModeContext);

  const form = Form.useFormInstance();
  const hotelId = Form.useWatch(['hotelId'], form);
  const countryId = Form.useWatch(['countryId'], form);
  const experienceType = Form.useWatch(['experienceType'], form);
  const openingConfiguration = Form.useWatch<OpeningConfiguration>(
    ['openingConfiguration'],
    form
  );

  const isAlwaysIncludedWithNight = Form.useWatch(
    'isAlwaysIncludedWithNight',
    form
  );

  const allowedPax = Form.useWatch<string[]>(['allowedPaxTypes'], form);

  useEffect(() => {
    if (!isAlwaysIncludedWithNight || !allowedPax) {
      return;
    }

    const priceConfig = (
      form.getFieldsValue(true) as {
        openingConfiguration?: OpeningConfiguration;
      }
    ).openingConfiguration?.priceConfiguration;

    if (!priceConfig || priceConfig.cost !== PRICE_COST_TYPE.PAID) {
      return;
    }

    const hasAdult = allowedPax.includes(PAX_TYPE.ADULT);
    const hasChild = allowedPax.includes(PAX_TYPE.CHILD);
    const mode = priceConfig.priceDifferentiationMode;

    const targetMode =
      hasChild &&
      (mode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD ||
        mode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME)
        ? PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME
        : PRICE_DIFFERENTIATION_MODE.WITH_DAYTIME;

    const allowsChildDaytime =
      hasChild && targetMode === PRICE_DIFFERENTIATION_MODE.WITH_CHILD_DAYTIME;

    const isSet = (value?: number | null): boolean =>
      value !== undefined && value !== null;

    // Always-included overnight forbids night-stay prices. Keep only the daytime
    // fields the target mode + allowed guests permit (migrating any entered
    // amount), and clear everything else so no hidden value blocks the save.
    const toDaytimeOnly = (
      config?: BasePriceConfiguration | null
    ): BasePriceConfiguration => {
      const current = config ?? {};

      return {
        ...current,
        price: undefined,
        childPrice: undefined,
        daytimePrice: hasAdult
          ? (current.daytimePrice ?? current.price)
          : undefined,
        childDaytimePrice: allowsChildDaytime
          ? (current.childDaytimePrice ?? current.childPrice)
          : undefined,
      };
    };

    const isCompliant = (config?: BasePriceConfiguration | null): boolean => {
      const current = config ?? {};

      return (
        !isSet(current.price) &&
        !isSet(current.childPrice) &&
        (hasAdult || !isSet(current.daytimePrice)) &&
        (allowsChildDaytime || !isSet(current.childDaytimePrice))
      );
    };

    if (priceConfig.type === OPENING_CONFIGURATION_TYPE.DAILY) {
      const days = priceConfig.days ?? {};
      const needsUpdate =
        mode !== targetMode ||
        Object.values(days).some((day) => !isCompliant(day));

      if (!needsUpdate) {
        return;
      }

      form.setFieldsValue({
        openingConfiguration: {
          priceConfiguration: {
            priceDifferentiationMode: targetMode,
            days: Object.fromEntries(
              Object.entries(days).map(([day, config]) => [
                day,
                toDaytimeOnly(config),
              ])
            ),
          },
        },
      });

      return;
    }

    if (mode === targetMode && isCompliant(priceConfig.configuration)) {
      return;
    }

    form.setFieldsValue({
      openingConfiguration: {
        priceConfiguration: {
          priceDifferentiationMode: targetMode,
          configuration: toDaytimeOnly(priceConfig.configuration),
        },
      },
    });
  }, [form, isAlwaysIncludedWithNight, openingConfiguration, allowedPax]);

  const isOriginal =
    !hotelId && countryId && experienceType === ExperienceType.STAYCATION;

  const hasFees =
    openingConfiguration?.priceConfiguration?.cost === PRICE_COST_TYPE.PAID;
  const configurationType: OpeningConfigurationType | undefined =
    openingConfiguration?.priceConfiguration?.type;

  return (
    <SCard
      title="Price"
      subtitle="Configure the activity price depending on day, type and guests."
      className="experience-form price-configuration"
    >
      <FormLineLayout isRequired label="Cost" disabled={isViewMode}>
        <Form.Item
          name={['openingConfiguration', 'priceConfiguration', 'cost']}
        >
          <Radio.Group disabled={isViewMode}>
            <Radio value={PRICE_COST_TYPE.FREE} disabled={isOriginal}>
              Free of charge
            </Radio>
            <Radio value={PRICE_COST_TYPE.PAID}>Applicable fees</Radio>
          </Radio.Group>
        </Form.Item>
      </FormLineLayout>

      {hasFees && (
        <>
          {isViewMode && (
            <Form.Item
              name={[
                'openingConfiguration',
                'priceConfiguration',
                'priceDifferentiationMode',
              ]}
              noStyle
            >
              <input type="hidden" />
            </Form.Item>
          )}
          <FormLineLayout
            isRequired
            label="Configuration"
            disabled={isViewMode}
          >
            <Form.Item
              name={['openingConfiguration', 'priceConfiguration', 'type']}
              rules={RULES}
            >
              <Radio.Group disabled={isViewMode}>
                <Radio value={OPENING_CONFIGURATION_TYPE.CONSTANT}>
                  Same every day
                </Radio>
                <Radio
                  value={OPENING_CONFIGURATION_TYPE.DAILY}
                  disabled={isOriginal}
                >
                  Depends on the day
                </Radio>
              </Radio.Group>
            </Form.Item>
          </FormLineLayout>
          {configurationType === OPENING_CONFIGURATION_TYPE.CONSTANT && (
            <ConstantPriceConfiguration isOriginal={isOriginal} />
          )}
          {configurationType === OPENING_CONFIGURATION_TYPE.DAILY && (
            <DailyPriceConfiguration />
          )}
        </>
      )}
    </SCard>
  );
};
